import { PureComponent, ReactNode } from 'react';

import { Commit } from 'src/components/types';
import urls from 'src/urls/source';
import authRequest from 'src/utils/fetch';

export type RenderProps = {
  commits: Commit[];
  hasError: boolean;
  hasMore: boolean;
  isLoading: boolean;
  loadMore: () => void;
};

type FileHistoryProviderProps = {
  pageLen?: number;
  path: string;
  refOrHash: string;
  isReadyToFetch?: boolean;
  render: (props: RenderProps) => ReactNode;
  repositoryFullSlug: string;
};

type FileHistoryProviderState = {
  commits: Commit[];
  hasError: boolean;
  isLoading: boolean;
  nextUrl: string | null | undefined;
};

export default class FileHistoryProvider extends PureComponent<
  FileHistoryProviderProps,
  FileHistoryProviderState
> {
  static defaultProps = {
    isReadyToFetch: true,
  };

  // eslint-disable-next-line react/sort-comp
  getInitialState = () => ({
    commits: [],
    hasError: false,
    isLoading: !this.props.isReadyToFetch,
    nextUrl: null,
  });

  state = this.getInitialState();

  componentDidMount() {
    if (this.props.isReadyToFetch) {
      this.fetchFileHistory();
    }
  }

  componentDidUpdate(prevProps: FileHistoryProviderProps) {
    const { path, refOrHash, repositoryFullSlug, isReadyToFetch } = this.props;
    if (
      path !== prevProps.path ||
      refOrHash !== prevProps.refOrHash ||
      repositoryFullSlug !== prevProps.repositoryFullSlug ||
      isReadyToFetch !== prevProps.isReadyToFetch
    ) {
      this.fetchFileHistory({ reset: true });
    }
  }

  fetchFileHistory = async (opts: { reset?: boolean } = {}) => {
    const stateUpdate = opts.reset ? this.getInitialState() : {};
    this.setState({ ...stateUpdate, isLoading: true });

    const url =
      this.state.nextUrl ||
      urls.api.v20.fileHistory(
        this.props.repositoryFullSlug,
        this.props.refOrHash,
        this.props.path,
        this.props.pageLen
      );

    try {
      const response = await fetch(authRequest(url));

      if (!response.ok) {
        throw Error();
      }

      const json = await response.json();

      this.setState(prevState => {
        const existingCommits = opts.reset ? [] : prevState.commits;
        // @ts-ignore TODO: fix noImplicitAny error here
        const newCommits = json.values.map(repofile => repofile.commit);
        return {
          commits: [...existingCommits, ...newCommits],
          nextUrl: json.next,
          hasError: false,
          isLoading: false,
        };
      });
    } catch {
      this.setState({
        hasError: true,
        isLoading: false,
      });
    }
  };

  loadMore = () => {
    this.fetchFileHistory();
  };

  render() {
    const { commits, hasError, isLoading, nextUrl } = this.state;

    return this.props.render({
      commits,
      hasMore: !!nextUrl,
      hasError,
      isLoading,
      loadMore: this.loadMore,
    });
  }
}
